/**
 * Kronos Timesheet page — Clockify-style editable weekly grid.
 *
 * Every week is editable — there is no time-based lock.
 *
 * The grid cells create / update / delete TimeEntry docs in Firestore.
 */

import { useCallback, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { Designation } from '@altyor-kronos/shared-types';
import { useAuth } from '../core/auth/useAuth';
import {
  deleteTimeEntry,
  createManualTimeEntry,
  updateTimeEntryFields,
} from '../core/data/kronosMutations';
import { attachCatalogDesignation } from '../core/data/projectMutations';
import WeeklyBreakdown from '../features/kronos/WeeklyBreakdown';
import {
  useMyEntriesInWindow,
  useAllProjects,
  useMultiProjectDesignations,
  useTrackerProjects,
  useProjectDesignations,
  useAllCatalog,
} from '../features/kronos/useKronosData';
import {
  getWeekRange,
  planCellChange,
  type CellEntry,
  type CellOp,
} from '../features/kronos/timesheet';
import ConfirmDeleteDialog from '../features/admin/ConfirmDeleteDialog';
import { sumDurationSeconds } from '../features/kronos/timeEntries';
import OrientationPrompt from '../layout/OrientationPrompt';
import {
  buildDesignationOptions,
  buildRecencyMap,
  decodeDesignationOptionId,
  matchExistingDesignation,
} from '../features/kronos/designationOptions';
import { colors } from '../core/theme/theme';
import { useAllUsers } from '../features/kronos/useKronosData';
import TeammateScopePicker from '../features/kronos/TeammateScopePicker';
import {
  canViewOtherUsersEntries,
  canEditOtherUsersEntries,
} from '../core/auth/entryScopeAccess';

export default function TimesheetView() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [anchor, setAnchor] = useState<Date>(() => new Date());
  const range = useMemo(() => getWeekRange(anchor), [anchor]);

  // ── Saisir pour un collègue (admin) ───────────────────────────
  // Consulter et modifier sont deux droits distincts : `direction` et
  // `finance` regardent, seul `admin` corrige — et le serveur estampille
  // alors la description à son nom.
  const canSeeOthers = canViewOtherUsersEntries(user?.roles);
  const canEditOthers = canEditOtherUsersEntries(user?.roles);
  const allUsers = useAllUsers(canSeeOthers);
  const [scopeUserId, setScopeUserId] = useState('');
  const targetUserId = scopeUserId || user?.uid || '';
  const isOtherUser = Boolean(scopeUserId && scopeUserId !== user?.uid);
  const readOnly = isOtherUser && !canEditOthers;
  const scopeUserName =
    allUsers.users.find((u) => u.id === scopeUserId)?.data.displayName ?? '';

  const weekEntries = useMyEntriesInWindow(targetUserId, range.start, range.end);

  const totalSeconds = useMemo(
    () => sumDurationSeconds(weekEntries.entries.map((e) => e.data)),
    [weekEntries.entries],
  );

  // ── Label lookups ─────────────────────────────────────────────
  const { projects: allProjects } = useAllProjects();
  const projectLabels = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of allProjects) m.set(p.id, `${p.data.code} — ${p.data.name}`);
    return m;
  }, [allProjects]);

  const uniqueProjectIds = useMemo(() => {
    const set = new Set<string>();
    for (const e of weekEntries.entries) set.add(e.data.projectId);
    return Array.from(set);
  }, [weekEntries.entries]);

  const designationLabels = useMultiProjectDesignations(uniqueProjectIds);

  // ── "Add row" picker data (hybrid: project designations + catalog) ──
  const assignedProjects = useTrackerProjects(user);
  const availableProjects = useMemo(
    () => assignedProjects.projects.map((p) => ({ id: p.id, label: `${p.data.code} — ${p.data.name}` })),
    [assignedProjects.projects],
  );

  const [addRowProjectId, setAddRowProjectId] = useState('');
  const addRowDesignations = useProjectDesignations(addRowProjectId);
  const { entries: catalog } = useAllCatalog();

  // Recently-used designations float into their own group at the top of
  // the picker, the project's full list staying visible below.
  // ponytail: recency read from the displayed week only (already loaded) —
  // widen to a 30-day window like the calendar if an empty week feels blind.
  const addRowRecentIds = useMemo(
    () => buildRecencyMap(weekEntries.entries.map((e) => e.data), addRowProjectId),
    [weekEntries.entries, addRowProjectId],
  );

  // Build hybrid options: existing project designations + catalog.
  const availableDesignations = useMemo(() => {
    const opts = buildDesignationOptions(
      addRowDesignations.designations.map((d) => d.data),
      catalog.map((c) => c.data),
      {
        sectionLabel: (s) => t(`projects.designations.sections.${s}`),
        existingGroupLabel: t('kronos.tracker.designationsOnProject'),
        recentGroupLabel: t('kronos.tracker.designationsRecent'),
      },
      addRowRecentIds,
    );
    return opts.map((o) => ({ id: o.id, label: o.label, group: o.groupLabel }));
  }, [addRowDesignations.designations, catalog, t, addRowRecentIds]);

  // Phantom rows managed here so we can resolve catalog IDs before
  // storing the real designationId.
  const [phantomRows, setPhantomRows] = useState<
    Array<{ projectId: string; designationId: string }>
  >([]);

  /**
   * Resolve an encoded picker option id to a real Firestore designation
   * doc id. If the option is a catalog pick that has no matching
   * designation on the project yet, we auto-attach it.
   */
  const resolveDesignationId = useCallback(
    async (encodedOptionId: string, projectId: string): Promise<string | null> => {
      const decoded = decodeDesignationOptionId(encodedOptionId);
      if (!decoded) return null;

      if (decoded.kind === 'existing') return decoded.id;
      if (decoded.kind === 'custom') return null;

      // Catalog pick → check if an existing designation already matches.
      const existingDesigs: Designation[] = addRowDesignations.designations.map((d) => d.data);
      const existing = matchExistingDesignation(existingDesigs, decoded.catalogId, decoded.variant);
      if (existing) return existing.id;

      // Auto-attach: create a new designation under the project.
      const catalogEntry = catalog.find((c) => c.id === decoded.catalogId)?.data;
      if (!catalogEntry) return null;

      const name = decoded.variant
        ? `${catalogEntry.name} — ${decoded.variant}`
        : catalogEntry.name;
      return attachCatalogDesignation(projectId, {
        section: catalogEntry.section,
        name,
        catalogId: decoded.catalogId,
        variant: decoded.variant,
      });
    },
    [addRowDesignations.designations, catalog],
  );

  const [cellError, setCellError] = useState<string | null>(null);

  const handleAddRow = useCallback(
    async (projectId: string, encodedDesignationId: string) => {
      try {
        const designationId = await resolveDesignationId(encodedDesignationId, projectId);
        if (!designationId) return;

        setPhantomRows((prev) => {
          const key = `${projectId}::${designationId}`;
          if (prev.some((p) => `${p.projectId}::${p.designationId}` === key)) return prev;
          return [...prev, { projectId, designationId }];
        });
      } catch {
        setCellError(t('kronos.timesheet.saveFailed'));
      }
    },
    [resolveDesignationId, t],
  );

  // ── Cell → entries index ──────────────────────────────────────
  const cellIndex = useMemo(() => {
    const map = new Map<string, CellEntry[]>();
    for (const { id, data } of weekEntries.entries) {
      const ms = data.startTime.toMillis();
      const dayIndex = Math.max(0, Math.min(6, Math.floor((ms - range.start.getTime()) / 86400000)));
      const key = `${data.projectId}::${data.designationId}::${dayIndex}`;
      const list = map.get(key) ?? [];
      list.push({ id, startMs: ms, durationSeconds: data.durationSeconds });
      map.set(key, list);
    }
    return map;
  }, [weekEntries.entries, range]);

  // ── Cell change handler ───────────────────────────────────────
  const applyOps = useCallback(
    async (projectId: string, designationId: string, ops: CellOp[]) => {
      try {
        await Promise.all(ops.map((op) => {
          if (op.kind === 'delete') return deleteTimeEntry(op.id);
          if (op.kind === 'update') {
            return updateTimeEntryFields(op.id, { durationSeconds: op.durationSeconds, endTime: op.endTime });
          }
          return createManualTimeEntry({
            userId: targetUserId,
            projectId,
            designationId,
            description: '',
            tags: [],
            startTime: op.start,
            endTime: op.end,
            durationSeconds: op.durationSeconds,
            billable: true,
          });
        }));
      } catch {
        setCellError(t('kronos.timesheet.saveFailed'));
      }
    },
    [targetUserId, t],
  );

  // A cell may hold several entries; anything that deletes one waits for an
  // explicit confirmation (#66: clearing a cell used to wipe the day silently).
  const [pendingDelete, setPendingDelete] = useState<
    { projectId: string; designationId: string; ops: CellOp[]; count: number } | null
  >(null);

  const handleCellChange = useCallback(
    async (projectId: string, designationId: string, dayIndex: number, newSeconds: number) => {
      if (!targetUserId || readOnly) return;
      setCellError(null);

      const existing = cellIndex.get(`${projectId}::${designationId}::${dayIndex}`) ?? [];
      const dayStart = new Date(
        range.start.getFullYear(),
        range.start.getMonth(),
        range.start.getDate() + dayIndex,
        9, 0, 0, 0,
      );
      const ops = planCellChange(existing, newSeconds, dayStart);
      const count = ops.filter((op) => op.kind === 'delete').length;
      if (count > 0) setPendingDelete({ projectId, designationId, ops, count });
      else if (ops.length > 0) await applyOps(projectId, designationId, ops);
    },
    [targetUserId, readOnly, cellIndex, range, applyOps],
  );

  // ── Navigation ────────────────────────────────────────────────
  const onPrev = () => setAnchor(new Date(anchor.getTime() - 7 * 86400000));
  const onNext = () => setAnchor(new Date(anchor.getTime() + 7 * 86400000));
  const onToday = () => setAnchor(new Date());

  if (!user) {
    return (
      <Box>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <OrientationPrompt>
    <Box>
      {/* Hero header with background image */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          mx: { xs: -2, sm: -3, md: -5 },
          mt: { xs: -2, sm: -3, md: -5 },
          mb: 3,
          px: { xs: 2, sm: 3, md: 5 },
          py: { xs: 3, sm: 4 },
          minHeight: 140,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
        }}
      >
        <Box
          component="img"
          src="/images/timesheet-header.webp"
          alt=""
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center 30%',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(20, 23, 28, 0.55)',
          }}
        />
        <Stack spacing={0.5} sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h2" sx={{ color: colors.white, fontWeight: 700 }}>
            {t('kronos.timesheet.title')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            {t('kronos.timesheet.subtitle')}
          </Typography>
        </Stack>
      </Box>

      {canSeeOthers && (
        <Stack
          direction="row"
          spacing={2}
          sx={{ alignItems: 'center', mb: 2, flexWrap: 'wrap' }}
        >
          <TeammateScopePicker
            users={allUsers.users}
            value={scopeUserId}
            onChange={setScopeUserId}
            placeholder={t('kronos.onBehalf.picker')}
          />
        </Stack>
      )}

      {isOtherUser && (
        <Alert severity={readOnly ? 'info' : 'warning'} sx={{ mb: 2 }}>
          {readOnly
            ? t('kronos.onBehalf.readOnly', { name: scopeUserName })
            : t('kronos.onBehalf.banner', { name: scopeUserName })}
        </Alert>
      )}

      <WeeklyBreakdown
        range={range}
        entries={weekEntries.entries.map((e) => e.data)}
        totalSeconds={totalSeconds}
        loading={weekEntries.loading}
        error={weekEntries.error ? t('kronos.timesheet.loadError') : null}
        onPrev={onPrev}
        onNext={onNext}
        onToday={onToday}
        projectLabels={projectLabels}
        designationLabels={designationLabels}
        onCellChange={readOnly ? undefined : handleCellChange}
        availableProjects={availableProjects}
        availableDesignations={availableDesignations}
        onAddRowProjectChange={setAddRowProjectId}
        onAddRow={handleAddRow}
        phantomRows={phantomRows}
        onRemoveRow={(pid, did) =>
          setPhantomRows((prev) =>
            prev.filter((p) => !(p.projectId === pid && p.designationId === did)),
          )
        }
      />

      <ConfirmDeleteDialog
        open={pendingDelete !== null}
        title={t('kronos.timesheet.confirmDeleteTitle')}
        body={t('kronos.timesheet.confirmDeleteBody', { count: pendingDelete?.count ?? 0 })}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => {
          if (!pendingDelete) return;
          const { projectId, designationId, ops } = pendingDelete;
          setPendingDelete(null);
          void applyOps(projectId, designationId, ops);
        }}
      />

      {cellError ? (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setCellError(null)}>
          {cellError}
        </Alert>
      ) : null}
    </Box>
    </OrientationPrompt>
  );
}
